import type { Language } from '@eformulieren/engine'

/**
 * Wanneer de toegang tot dit formulier is beëindigd (R-66, R-155b, ontwerp 6.4 en 9.2).
 *
 * Trekt de deler een deling in, of haalt het processysteem een toekenning weg, dan sluit de pod de
 * samenwerkverbinding met `geen-recht`: binnen tien seconden voor wie schrijft
 * (`PERMISSIONS_VALID_MS`) en binnen twintig voor wie alleen kijkt (de periodieke ronde). Die sluiting
 * is eindstand (`CLOSE_REASONS` in `samenwerken.ts`): de client hoort niet opnieuw te verbinden, en
 * het scherm hoort niet een formulier te laten staan dat bewerkbaar lijkt. Zie de tabel in 6.4: de
 * melding blijft tot de gebruiker de pagina ververst.
 *
 * **Twee vormen, en dat is een gegeven van Hocuspocus.** Sluit de pod de socket zelf, dan komt de
 * code 4001 met de reden mee. Sluit hij één document op een gedeelde socket, dan stuurt de server
 * een sluitbericht, en de provider maakt daar code 1000 met de reden van (`MessageReceiver.ts` in
 * `@hocuspocus/provider`). De reden is in beide gevallen `geen-recht`.
 *
 * Dezelfde sluiting komt ook als het register langer dan een minuut niet te bereiken is
 * (`PERMISSIONS_UNCONFIRMED_MAX_MS`); de pod neemt dan niets meer aan op een recht dat niet is
 * vastgesteld. Daarom zegt de melding erbij dat verversen de weg terug is.
 */

const NO_PERMISSION = { code: 4001, reden: 'geen-recht' } as const
/**
 * Sinds besluit 9 van 30 oktober 2026 zegt de pod waarom (`CLOSE_REASONS` in `collaboration.ts`):
 * alle toegang weg sluit de socket met `toegang-ingetrokken`, een sectie die wegvalt terwijl de rest
 * blijft sluit alleen haar document met `toegang-gewijzigd`.
 */
const WITHDRAWN = { code: 4005, reden: 'toegang-ingetrokken' } as const
const CHANGED = { code: 4006, reden: 'toegang-gewijzigd' } as const

type Closing = { readonly code?: number; readonly reason?: string } | undefined

function is(closing: NonNullable<Closing>, kind: { readonly code: number; readonly reden: string }): boolean {
  return closing.code === kind.code || closing.reason === kind.reden
}

/**
 * Of de toegang (geheel of voor deze sectie) voorbij is. Een gewijzigde toegang valt er ook onder: tot
 * de pagina daar een eigen weergave voor heeft, toont ze dezelfde melding als toen de pod nog de hele
 * socket sloot. Met `accessChangeOf` leest de pagina het onderscheid.
 */
export function isEndOfAccess(closing: Closing): boolean {
  if (closing === undefined) return false
  return is(closing, NO_PERMISSION) || is(closing, WITHDRAWN) || is(closing, CHANGED)
}

/** Welke verandering de pod meldt: alle toegang ingetrokken, of een deel gewijzigd. */
export function accessChangeOf(closing: Closing): 'ingetrokken' | 'gewijzigd' | undefined {
  if (closing === undefined) return undefined
  if (is(closing, WITHDRAWN)) return 'ingetrokken'
  if (is(closing, CHANGED)) return 'gewijzigd'
  return undefined
}

const NOTICE: Readonly<Record<Language, string>> = {
  nl: 'Uw toegang tot dit formulier is beëindigd. Denkt u dat u nog toegang hoort te hebben, ververs dan de pagina.',
  en: 'Your access to this form has ended. If you think you should still have access, refresh the page.',
}

export function accessEndedNotice(language: Language): string {
  return NOTICE[language]
}

/**
 * Het bericht waarmee de pod een sectie op een open verbinding naar lezen zet (besluit 9 van 30 oktober
 * 2026, api-contract afwijking 9): `{"soort":"recht","sectie":"<id>","recht":"lezen"}`, één keer, zonder
 * de verbinding te sluiten. Al het andere is geen verlaging.
 */
export function rightChangeOf(payload: string): { readonly sectie: string; readonly recht: 'lezen' } | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(payload)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined
  const message = parsed as { soort?: unknown; sectie?: unknown; recht?: unknown }
  if (message.soort !== 'recht' || typeof message.sectie !== 'string' || message.recht !== 'lezen') return undefined
  return { sectie: message.sectie, recht: 'lezen' }
}
